"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Sidebar from "./Sidebar";
import ImpersonationBanner from "./ImpersonationBanner";
import { useAuth } from "@/context/AuthContext";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";
import isPermitted from "@/utils/isPermitted";
import { isPublicRoute } from "@/constants/routeGuards";
import { useSidebarStore } from "@/store/sidebarStore";

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const pathname = usePathname();
  const { user, allPermissions } = useAuth();
  const { isOpen, closeSidebar } = useSidebarStore();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    closeSidebar();
  }, [pathname, closeSidebar]);

  const isTraining = pathname.startsWith("/training");
  const isAuthRoute = isPublicRoute(pathname);

  // Check if user can view sidebar
  const canViewSidebar = user
    ? isPermitted(user, PAGE_ROUTES.SIDEBAR, allPermissions)
    : false;

  // Don't show sidebar on training routes or login
  if (isTraining || isAuthRoute) {
    return (
      <>
        <ImpersonationBanner />
        {children}
      </>
    );
  }

  // Don't show sidebar if user doesn't have permission
  if (!canViewSidebar) {
    return (
      <>
        <ImpersonationBanner />
        {children}
      </>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <div className="bg-transparent h-app-height overflow-x-clip flex items-stretch justify-start">
      {/* Mobile backdrop — only rendered when the drawer is open below md */}
      {isOpen && (
        <div
          onClick={closeSidebar}
          aria-hidden="true"
          className="md:hidden fixed inset-x-0 top-[68px] bottom-0 bg-black/40 z-30"
        />
      )}

      {/* Sidebar: off-canvas drawer below md, inline column from md up */}
      <div
        className={`app-sidebar-shell w-64 min-w-64 shrink-0 bg-white z-40
          max-md:fixed max-md:top-[68px] max-md:bottom-0 max-md:left-0 max-md:shadow-xl
          max-md:transition-transform max-md:duration-300 max-md:ease-in-out
          ${isOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"}`}
      >
        <Sidebar />
      </div>

      <div className="min-w-0 flex-1 overflow-x-auto">{children}</div>
      </div>
    </>
  );
}
